import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { isCompleted } from '@/constants/matchStatus';
import { getStageLabel } from '@/utils/matchdayHelpers';
import { getDateLocale } from '@/utils/dateLocale';
import type { ParticipantBreakdownMatch } from '@/services/api';
import styles from './styles.module.scss';

type PredictedOutcome = 'team1_win' | 'draw' | 'team2_win';

export interface ParticipantMatchRowProps {
  match: ParticipantBreakdownMatch;
  /** True when the viewer is looking at their own statistics page. */
  isOwn: boolean;
}

// Coefficient values <= 1 are not real coefficients (project rule, mirrored
// from MatchPreviewCard.formatCoeff / CoefficientsCard.isDefaultCoefficient).
const formatCoeff = (raw: number | null | undefined): string => {
  if (raw === null || raw === undefined) return '—';
  if (!Number.isFinite(raw) || raw <= 1) return '—';
  return raw.toFixed(2);
};

// Resolve the outcome coefficient corresponding to the user's predicted side.
const getOutcomeCoeff = (
  outcome: PredictedOutcome | undefined,
  coefficients: ParticipantBreakdownMatch['coefficients'],
): number | null | undefined => {
  switch (outcome) {
    case 'team1_win': return coefficients.team1_win_coefficient;
    case 'draw':      return coefficients.draw_coefficient;
    case 'team2_win': return coefficients.team2_win_coefficient;
    default:          return undefined;
  }
};

/**
 * Per-match card on the participant statistics page. Three bands separated by
 * `1px solid var(--hairline)`:
 *   Band 1 — match identity (meta + teams + score/time).
 *   Band 2 — user prediction (label + score) and coefficient pair (С / И).
 *   Band 3 — bookmaker name on the left, points result on the right.
 *
 * If the requester is not the row's owner AND the match hasn't kicked off
 * yet, the backend returns `prediction = null` (visibility gate). In that
 * state Band 2 collapses to a centered hidden notice and Band 3 hides.
 */
export const ParticipantMatchRow: React.FC<ParticipantMatchRowProps> = ({ match, isOwn }) => {
  const { t } = useTranslation(['tournament']);

  const {
    match_date,
    status,
    stage,
    matchday,
    team1,
    team2,
    actual_score,
    prediction,
    coefficients,
    points_breakdown,
  } = match;

  const isFinished = isCompleted(status);

  // ─── Band 1 — meta line ──────────────────────────────────────
  const stageLabel = stage ? getStageLabel(stage) : null;
  const matchdayLabel = matchday != null
    ? t('tournament:participantStats.matchdayShort', { count: matchday })
    : null;
  const metaLeftCluster = [stageLabel, matchdayLabel].filter(Boolean).join(' · ');

  const locale = getDateLocale();
  const kickoffDate = new Date(match_date);
  const kickoffMeta = format(kickoffDate, 'd MMM · HH:mm', { locale });
  const kickoffTime = format(kickoffDate, 'HH:mm', { locale });

  const isPredictionGated = prediction === null;

  // ─── Band 3 — points state ───────────────────────────────────
  // Pending = the match isn't finished yet (and the user has a prediction).
  // Win = the user earned > 0 points on this match.
  // Loss = the match is finished and the user earned 0 points.
  const total = points_breakdown?.total_points ?? 0;
  const isPending = !isPredictionGated && prediction !== null && !isFinished;
  const isWin = isFinished && prediction !== null && total > 0;
  const isLoss = isFinished && prediction !== null && total === 0;

  // Bookmaker attribution — display only when priority resolution surfaced one.
  const showBookmaker = coefficients.selection === 'priority'
    && !!coefficients.source_bookmaker
    && !isPredictionGated;

  // ─── Band 2 — coefficient pair ───────────────────────────────
  const scoreCoeffRaw = prediction?.odds_score;
  const outcomeCoeffRaw = getOutcomeCoeff(prediction?.predicted_outcome, coefficients);
  const scoreCoeff = formatCoeff(scoreCoeffRaw);
  const outcomeCoeff = formatCoeff(outcomeCoeffRaw);

  const coeffsAria = t('tournament:participantStats.matchCard.pointsAria', {
    score: scoreCoeff,
    outcome: outcomeCoeff,
  });

  // ─── Left status rail color ──────────────────────────────────
  // win → grass, pending → azure. Loss and the visibility-gated state both
  // fall through to the neutral hairline rail (default `.card::before`).
  const railClass = isWin
    ? styles.railWin
    : isPending
      ? styles.railPending
      : undefined;

  return (
    <article
      className={[styles.card, railClass].filter(Boolean).join(' ')}
      data-match-id={match.match_id}
    >
      <div className={styles.content}>
      {/* ─── Band 1 — match identity ──────────────────────── */}
      <div className={styles.bandIdentity}>
        <div className={styles.meta}>
          {metaLeftCluster && <span className={styles.metaLeft}>{metaLeftCluster}</span>}
          <span className={styles.metaRight}>{kickoffMeta}</span>
        </div>

        <div className={styles.teamsRow}>
          {/* Team 1 — name (right-aligned) + logo */}
          <div className={`${styles.team} ${styles.teamLeft}`}>
            <h3 className={styles.teamName}>{team1.name}</h3>
            {team1.logo_url ? (
              <img
                src={team1.logo_url}
                alt={team1.name}
                className={styles.teamLogo}
                width={24}
                height={24}
                decoding="async"
              />
            ) : (
              <div className={styles.teamLogoPlaceholder} aria-hidden="true" />
            )}
          </div>

          {/* Centre — finished score or kickoff time */}
          <div className={styles.centerCol}>
            {isFinished && actual_score ? (
              <div className={styles.scoreValue}>
                <span>{actual_score.team1}</span>
                <span className={styles.scoreSep}>:</span>
                <span>{actual_score.team2}</span>
              </div>
            ) : (
              <span className={styles.kickoff}>{kickoffTime}</span>
            )}
          </div>

          {/* Team 2 — logo + name (left-aligned) */}
          <div className={`${styles.team} ${styles.teamRight}`}>
            {team2.logo_url ? (
              <img
                src={team2.logo_url}
                alt={team2.name}
                className={styles.teamLogo}
                width={24}
                height={24}
                decoding="async"
              />
            ) : (
              <div className={styles.teamLogoPlaceholder} aria-hidden="true" />
            )}
            <h3 className={styles.teamName}>{team2.name}</h3>
          </div>
        </div>
      </div>

      {/* ─── Band 2 — prediction + coefficients ───────────── */}
      {isPredictionGated ? (
        <div className={styles.bandHidden}>
          <span className={styles.hiddenText}>
            {t('tournament:participantStats.predictionHidden')}
          </span>
        </div>
      ) : prediction ? (
        <div className={styles.bandPrediction}>
          <div className={styles.predictionLeft}>
            <span className={styles.predictionLabel}>
              {t(`tournament:participantStats.matchCard.${isOwn ? 'yourPrediction' : 'prediction'}`)}
            </span>
            <span className={styles.predictionScore}>
              <span>{prediction.predicted_score_team1}</span>
              <span className={styles.scoreSep}>:</span>
              <span>{prediction.predicted_score_team2}</span>
            </span>
          </div>
          <div className={styles.coeffPair} aria-label={coeffsAria}>
            <span className={styles.coeffEntry}>
              <span className={styles.coeffLabel}>
                {t('tournament:participantStats.matchCard.scoreShort')}:
              </span>
              <span className={styles.coeffValue}>{scoreCoeff}</span>
            </span>
            <span className={styles.coeffEntry}>
              <span className={styles.coeffLabel}>
                {t('tournament:participantStats.matchCard.resultShort')}:
              </span>
              <span className={styles.coeffValue}>{outcomeCoeff}</span>
            </span>
          </div>
        </div>
      ) : null}

      {/* ─── Band 3 — bookmaker + result (hidden when gated) ── */}
      {!isPredictionGated && (
        <div className={styles.bandResult}>
          <span className={styles.bookmaker}>
            {showBookmaker ? coefficients.source_bookmaker : ''}
          </span>
          <div className={styles.resultRight}>
            {isPending && (
              <span className={styles.pendingPill}>
                {t('tournament:participantStats.matchCard.pendingPill')}
              </span>
            )}
            {isWin && (
              <div className={styles.winValue}>
                <i className={`icon-chevrons-up ${styles.winIcon}`} aria-hidden="true" />
                <span className={styles.winLabel}>
                  +{t('tournament:participantStats.matchCard.pointsValue', {
                    value: total.toFixed(2),
                  })}
                </span>
              </div>
            )}
            {isLoss && (
              <span className={styles.lossLabel}>
                {t('tournament:participantStats.matchCard.pointsValue', {
                  value: (0).toFixed(2),
                })}
              </span>
            )}
          </div>
        </div>
      )}
      </div>
    </article>
  );
};

export default ParticipantMatchRow;
